import React, { useEffect } from 'react';
import AddressInputStep from './Funnel/AddressInputStep';
import SolarMapStep from './Funnel/SolarMapStep';
import EnterDetailsStep from './Funnel/EnterDetailsStep.tsx';
import SolarConfiguratorStep from './Funnel/SolarConfiguratorStep';
import { type Libraries, useJsApiLoader } from "@react-google-maps/api";
import { type FallbackProps } from "react-error-boundary";
import FunnelLayout from "./Funnel/FunnelLayout";
import { ErrorBoundary } from "react-error-boundary";
import { MapsLoadedContext } from "../types/map.ts";
import { projectManager } from "../lib/Solar/ProjectManager";
import {
    FunnelDataProvider,
    useAddressData,
    useFunnelNavigation,
} from "../context/FunnelDataContext";
import { FUNNEL_STEP_IDS } from "../constants/funnelSteps";
import { createLogger } from "../lib/logger.ts";

const logger = createLogger("SolarConfiguratorFunnel");

const libraries: Libraries = ['places'];

const funnelSteps: FunnelStep[] = [
    {
        id: FUNNEL_STEP_IDS.ADDRESS_INPUT,
        title: 'Adresseingabe',
        component: AddressInputStep,
    },
    {
        id: FUNNEL_STEP_IDS.PRODUCT_SELECTION,
        title: 'Adresse prüfen',
        component: SolarMapStep,
    },
    {
        id: FUNNEL_STEP_IDS.DETAILS_INPUT,
        title: 'Details angeben',
        component: EnterDetailsStep,
    },
];

const configuratorStep: FunnelStep = {
    id: FUNNEL_STEP_IDS.SOLAR_CONFIGURATOR,
    title: 'Konfiguration',
    component: SolarConfiguratorStep,
};

const FunnelShell: React.FC = () => {
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: import.meta.env.VITE_GOOGLE_API_KEY!,
        libraries: libraries,
    });

    const { currentStepIndex, currentFunnelStep, isAtConfiguratorStep, goToStep, reset } =
        useFunnelNavigation();
    const addressData = useAddressData();

    // Activate ProjectManager when funnel data contains address information
    useEffect(() => {
        if (addressData?.currentAddress && !projectManager.isActivated()) {
            logger.debug('Activating ProjectManager with existing address data', {
                currentAddress: addressData.currentAddress,
            });
            projectManager.activate(addressData.currentAddress);
        }
    }, [addressData]);

    const fallbackRender = ({ resetErrorBoundary }: FallbackProps) => {
        resetErrorBoundary();

        return <div>Etwas ist schiefgelaufen. Bitte versuche es erneut.</div>;
    };

    const onErrorBoundaryReset = (e: unknown) => {
        logger.debug('Reset funnel data...', { event: e });
        reset();
    };

    if (isAtConfiguratorStep) {
        return <SolarConfiguratorStep />;
    }

    return (
        <ErrorBoundary fallbackRender={fallbackRender} onReset={onErrorBoundaryReset}>
            <MapsLoadedContext value={{ isLoaded: isLoaded, loadError: loadError }}>
                <FunnelLayout
                    currentStep={currentStepIndex}
                    funnelSteps={funnelSteps}
                    currentFunnelStep={currentFunnelStep}
                    onStepperClick={goToStep}
                />
            </MapsLoadedContext>
        </ErrorBoundary>
    );
};

const SolarConfiguratorFunnel: React.FC = () => {
    return (
        <FunnelDataProvider funnelSteps={funnelSteps} configuratorStep={configuratorStep}>
            <FunnelShell />
        </FunnelDataProvider>
    );
};

export default SolarConfiguratorFunnel;
